import React from 'react';
import { VStack, Box } from '@gluestack-ui/themed';
import { FormProvider, useForm } from 'react-hook-form';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useAuthAdminSetPassword } from '../../../../common/api/endpoints/auth/auth';
import { useDialog } from '../../../../common/context/DialogContext';
import { useApiErrorReporter } from '../../../../common/utils/apiErrorReporter';
import FormField from '../../../../common/components/forms/FormField';
import { PopupDialog } from '../../../../common/components/PopupDialog';

const AdminSetPasswordModal = ({ isOpen, onClose, userId }) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const adminSetPasswordMutation = useAuthAdminSetPassword();

  const methods = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    watch,
    reset,
  } = methods;

  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    try {
      await adminSetPasswordMutation.mutateAsync(
        {
          userId,
          data: {
            newPassword: data.newPassword,
          },
        },
        {
          onSuccess: () => {
            showToastMessage('success', t('admin.userDetails.setPasswordSuccess'));
            reset();
            onClose();
          },
          onError: (error) => {
            // Same AuthErrors contract as ChangePasswordModal (FLARE-6B).
            // admin_set_password only returns these two: policy is HTTP 400,
            // a concurrent account write is HTTP 409. Both are shown to the
            // admin; neither is a production defect.
            reportApiError(
              error,
              t('admin.userDetails.setPasswordTitle'),
              'toast',
              {
                expectedErrorCodes: [
                  'AUTH_PASSWORD_POLICY_VIOLATED',
                  'AUTH_USER_CHANGED',
                ],
              },
            );
          },
        },
      );
    } catch (error) {
      // Errors are handled in onError callback
    }
  };

  const buttons = [
    {
      text: t('common.buttons.cancel'),
      variant: 'outline',
      action: 'secondary',
      onPress: onClose,
    },
    {
      text: t('common.buttons.save'),
      action: 'primary',
      onPress: handleSubmit(onSubmit),
      isDisabled: adminSetPasswordMutation.isPending,
      preventAutoClose: true,
    },
  ];

  return (
    <PopupDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('admin.userDetails.setPasswordTitle')}
      message=""
      buttons={buttons}
    >
      <FormProvider {...methods}>
        <Box as={Platform.OS === 'web' ? 'form' : 'div'}>
          <VStack space="md">
            <FormField
              name="newPassword"
              type="password"
              label={t('admin.userDetails.newPassword')}
              isRequired
              autoComplete="new-password"
              rules={{
                required: t('common.errors.required'),
                minLength: { value: 8, message: t('common.errors.minLength', { count: 8 }) },
              }}
            />

            <FormField
              name="confirmPassword"
              type="password"
              label={t('admin.userDetails.confirmPassword')}
              isRequired
              autoComplete="new-password"
              rules={{
                required: t('common.errors.required'),
                validate: (value) => value === newPassword || t('common.errors.passwordMismatch'),
              }}
            />
          </VStack>
        </Box>
      </FormProvider>
    </PopupDialog>
  );
};

export default AdminSetPasswordModal;
